<template>
  <div class="inventory-page pb-5">
    <nav-bar />

    <main class="inventory-page__main">
      <header class="inventory-hero">
        <div class="inventory-hero__copy">
          <span class="inventory-hero__eyebrow">{{ $t('Inventory.vehicle_storage') }}</span>
          <h1>{{ $t('Inventory.title') }}</h1>
          <p>{{ $t('Inventory.page_description') }}</p>
        </div>
        <div class="inventory-hero__actions">
          <button
            type="button"
            class="btn inventory-button inventory-button--secondary"
            :disabled="loading || !activeInventory.length"
            @click="showExportModal = true"
          >
            <i class="fa fa-file-excel-o" aria-hidden="true" />
            {{ $t('Inventory.export') }}
          </button>
          <button
            v-if="canWrite"
            type="button"
            class="btn inventory-button inventory-button--secondary"
            @click="showAddModal = true"
          >
            <i class="fa fa-plus-circle" aria-hidden="true" />
            {{ $t('Inventory.add_car') }}
          </button>
          <button
            v-if="canWrite"
            type="button"
            class="btn inventory-button inventory-button--primary"
            :disabled="!selectedOrders.length"
            @click="showLeavingModal = true"
          >
            <i class="fa fa-sign-out" aria-hidden="true" />
            {{ $t('Inventory.car_is_leaving') }}
            <span v-if="selectedOrders.length" class="inventory-button__count">{{ selectedOrders.length }}</span>
          </button>
        </div>
      </header>

      <section class="inventory-stats" :aria-label="$t('Inventory.inventory_summary')">
        <div class="inventory-stat inventory-stat--inside">
          <i class="fa fa-map-marker" aria-hidden="true" />
          <div>
            <strong>{{ insideCount }}</strong>
            <span>{{ $t('Inventory.state_inside') }}</span>
          </div>
        </div>
        <div class="inventory-stat inventory-stat--left">
          <i class="fa fa-clock-o" aria-hidden="true" />
          <div>
            <strong>{{ leftCount }}</strong>
            <span>{{ $t('Inventory.state_left') }}</span>
          </div>
        </div>
        <div class="inventory-stat inventory-stat--visible">
          <i class="fa fa-car" aria-hidden="true" />
          <div>
            <strong>{{ filteredInventory.length }}</strong>
            <span>{{ $t('Inventory.results') }}</span>
          </div>
        </div>
      </section>

      <div v-if="isViewer" class="inventory-read-only">
        <i class="fa fa-eye" aria-hidden="true" />
        {{ $t('Inventory.read_only_notice') }}
      </div>

      <div v-if="referenceError" class="inventory-page__notice inventory-page__notice--warning" role="alert">
        <i class="fa fa-exclamation-triangle" aria-hidden="true" />
        {{ referenceError }}
      </div>

      <div v-if="pageFeedback" class="inventory-page__notice" :class="pageFeedbackClass" role="status">
        <i :class="['fa', pageFeedbackType === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle']" aria-hidden="true" />
        {{ pageFeedback }}
        <button type="button" class="close" :aria-label="$t('Inventory.close')" @click="pageFeedback = ''">&times;</button>
      </div>

      <section class="inventory-controls" :aria-label="$t('Inventory.inventory_filters')">
        <label class="inventory-search">
          <i class="fa fa-search" aria-hidden="true" />
          <span class="sr-only">{{ $t('Inventory.search') }}</span>
          <input
            v-model.trim="searchQuery"
            type="search"
            :placeholder="$t('Inventory.search_placeholder')"
          >
          <button v-if="searchQuery" type="button" :aria-label="$t('Inventory.clear_search')" @click="searchQuery = ''">
            <i class="fa fa-times" aria-hidden="true" />
          </button>
        </label>

        <div class="inventory-state-filter" role="group" :aria-label="$t('Inventory.inventory_state')">
          <button
            v-for="option in stateOptions"
            :key="option.value"
            type="button"
            :class="{'is-active': stateFilter === option.value}"
            @click="stateFilter = option.value"
          >
            {{ option.label }}
            <span>{{ option.count }}</span>
          </button>
        </div>

        <label class="inventory-sort">
          <span>{{ $t('Inventory.sort_by') }}</span>
          <select v-model="sortBy">
            <option value="newest">{{ $t('Inventory.sort_newest') }}</option>
            <option value="oldest">{{ $t('Inventory.sort_oldest') }}</option>
            <option value="billing">{{ $t('Inventory.sort_billing_name') }}</option>
          </select>
        </label>

        <div class="inventory-view-toggle" role="group" :aria-label="$t('Inventory.view_mode')">
          <button
            type="button"
            :class="{'is-active': viewMode === 'cards'}"
            :aria-pressed="viewMode === 'cards'"
            @click="viewMode = 'cards'"
          >
            <i class="fa fa-th-large" aria-hidden="true" />
            <span>{{ $t('Inventory.card_view') }}</span>
          </button>
          <button
            type="button"
            :class="{'is-active': viewMode === 'list'}"
            :aria-pressed="viewMode === 'list'"
            @click="viewMode = 'list'"
          >
            <i class="fa fa-list" aria-hidden="true" />
            <span>{{ $t('Inventory.list_view') }}</span>
          </button>
        </div>

        <div
          class="inventory-lot-filter inventory-term-filter"
          role="group"
          :aria-label="$t('Inventory.storage_term')"
        >
          <span class="inventory-lot-filter__label">
            <i class="fa fa-clock-o" aria-hidden="true" />
            {{ $t('Inventory.storage_term') }}
          </span>
          <div class="inventory-lot-filter__options">
            <button
              v-for="option in termOptions"
              :key="option.value"
              type="button"
              :class="{'is-active': termFilter === option.value}"
              @click="termFilter = option.value"
            >
              {{ option.label }}
              <span>{{ option.count }}</span>
            </button>
          </div>
        </div>

        <div class="inventory-lot-filter" role="group" :aria-label="$t('Inventory.filter_by_lot')">
          <span class="inventory-lot-filter__label">
            <i class="fa fa-map-marker" aria-hidden="true" />
            {{ $t('Inventory.filter_by_lot') }}
          </span>
          <div class="inventory-lot-filter__options">
            <span v-if="lotsLoaded && !lotNames.length" class="inventory-lot-filter__empty">
              {{ $t('Inventory.no_lots_configured') }}
              <template v-if="!canManageLots">{{ $t('Inventory.no_lots_help') }}</template>
            </span>
            <button
              v-for="option in lotOptions"
              :key="option.value"
              type="button"
              :class="{'is-active': lotFilter === option.value}"
              @click="lotFilter = option.value"
            >
              {{ option.label }}
              <span>{{ option.count }}</span>
            </button>
            <button
              v-if="canManageLots"
              type="button"
              class="inventory-lot-filter__manage"
              @click="showLotsModal = true"
            >
              <i class="fa fa-cog" aria-hidden="true" />
              {{ $t('Inventory.manage_lots') }}
            </button>
          </div>
        </div>
      </section>

      <div v-if="canWrite && selectedActionCount" class="inventory-selection-bar">
        <div>
          <i class="fa fa-check-square-o" aria-hidden="true" />
          <strong>{{ $t('Inventory.selected_count', {count: selectedActionCount}) }}</strong>
        </div>
        <div>
          <button type="button" class="btn btn-light" @click="clearSelection">
            {{ $t('Inventory.clear_selection') }}
          </button>
          <button
            v-if="selectedConfirmationOrders.length"
            type="button"
            class="btn btn-kne-green"
            :disabled="confirmingSelectedArrivals"
            @click="confirmSelectedArrivals"
          >
            <i :class="['fa', confirmingSelectedArrivals ? 'fa-spinner fa-spin' : 'fa-check']" aria-hidden="true" />
            {{ $t('Inventory.confirm_selected', {count: selectedConfirmationOrders.length}) }}
          </button>
          <button
            v-if="selectedOrders.length"
            type="button"
            class="btn btn-kne-orange"
            @click="showLeavingModal = true"
          >
            {{ $t('Inventory.car_is_leaving') }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="inventory-page__loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true" />
        <span>{{ $t('Inventory.loading_inventory') }}</span>
      </div>

      <div v-else-if="!activeInventory.length" class="inventory-empty">
        <div class="inventory-empty__icon"><i class="fa fa-car" aria-hidden="true" /></div>
        <h2>{{ $t('Inventory.empty_title') }}</h2>
        <p>{{ $t('Inventory.empty_help') }}</p>
        <button v-if="canWrite" type="button" class="btn btn-kne-orange" @click="showAddModal = true">
          <i class="fa fa-plus-circle mr-1" aria-hidden="true" />
          {{ $t('Inventory.add_first_car') }}
        </button>
      </div>

      <div v-else-if="!filteredInventory.length" class="inventory-empty inventory-empty--compact">
        <div class="inventory-empty__icon"><i class="fa fa-search" aria-hidden="true" /></div>
        <h2>{{ $t('Inventory.no_results_title') }}</h2>
        <p>{{ $t('Inventory.no_results_help') }}</p>
        <button type="button" class="btn btn-light" @click="clearFilters">
          {{ $t('Inventory.clear_filters') }}
        </button>
      </div>

      <section
        v-else-if="viewMode === 'cards'"
        class="inventory-grid"
        :aria-label="$t('Inventory.inventory_vehicles')"
      >
        <inventory-card
          v-for="order in filteredInventory"
          :key="orderId(order)"
          :order="order"
          :image-url="imageFor(order)"
          :selected="selectedIds.includes(orderId(order))"
          :can-write="canWrite"
          :confirming="confirmingOrderIds.includes(orderId(order))"
          :updating-lot="updatingLotOrderIds.includes(orderId(order))"
          @toggle-selection="toggleSelection"
          @confirm-arrival="confirmArrival"
          @change-lot="changeLot"
          @bill="openBillingModal"
        />
      </section>

      <section v-else class="inventory-list" :aria-label="$t('Inventory.inventory_vehicles')">
        <inventory-list-item
          v-for="order in filteredInventory"
          :key="orderId(order)"
          :order="order"
          :image-url="imageFor(order)"
          :selected="selectedIds.includes(orderId(order))"
          :can-write="canWrite"
          :confirming="confirmingOrderIds.includes(orderId(order))"
          :updating-lot="updatingLotOrderIds.includes(orderId(order))"
          @toggle-selection="toggleSelection"
          @confirm-arrival="confirmArrival"
          @change-lot="changeLot"
          @bill="openBillingModal"
        />
      </section>
    </main>

    <add-inventory-cars-modal
      :open="showAddModal"
      :accounts="accounts"
      :drivers="drivers"
      :existing-orders="activeInventory"
      @close="showAddModal = false"
      @completed="onCreationCompleted"
    />

    <inventory-leaving-modal
      :open="showLeavingModal"
      :orders="selectedOrders"
      :drivers="drivers"
      @close="showLeavingModal = false"
      @completed="onLeavingCompleted"
    />

    <inventory-export-modal
      :open="showExportModal"
      :orders="activeInventory"
      @close="showExportModal = false"
    />

    <inventory-lots-modal
      v-if="canManageLots"
      :open="showLotsModal"
      :orders="activeInventory"
      @close="showLotsModal = false"
    />

    <inventory-billing-done-modal
      :open="showBillingModal"
      :order="billingOrder"
      @close="closeBillingModal"
      @completed="onBillingCompleted"
    />
  </div>
</template>

<script>
import NavBar from '../module/interface-components/NavBar.vue'
import InventoryCard from '../module/special-components/Inventory/InventoryCard.vue'
import InventoryListItem from '../module/special-components/Inventory/InventoryListItem.vue'
import AddInventoryCarsModal from '../module/special-components/Inventory/AddInventoryCarsModal.vue'
import InventoryLeavingModal from '../module/special-components/Inventory/InventoryLeavingModal.vue'
import InventoryBillingDoneModal from '../module/special-components/Inventory/InventoryBillingDoneModal.vue'
import InventoryLotsModal from '../module/special-components/Inventory/InventoryLotsModal.vue'
import InventoryExportModal from '../module/special-components/Inventory/InventoryExportModal.vue'
import { db } from '../../firebase'
import { confirmInventoryArrival, updateInventoryLot } from '../../inventory/inventoryService'
import {
  INVENTORY_TERM,
  INVENTORY_TERMS,
  isInventoryTerm,
  isInventoryOrder,
  isActiveInventoryOrder,
  canConfirmInventoryArrival,
  canLeaveInventory,
  getInventoryImage,
  normalizeInventoryLotName
} from '../../inventory/inventorySelectors'

const ALL_LOTS_FILTER = 'all'
const UNASSIGNED_LOT_FILTER = '__unassigned__'
const ALL_TERMS_FILTER = 'all'
const UNASSIGNED_TERM_FILTER = '__unassigned__'

export default {
  name: 'InventoryPage',

  components: {
    NavBar,
    InventoryCard,
    InventoryListItem,
    AddInventoryCarsModal,
    InventoryLeavingModal,
    InventoryBillingDoneModal,
    InventoryLotsModal,
    InventoryExportModal
  },

  data () {
    return {
      searchQuery: '',
      stateFilter: 'all',
      lotFilter: ALL_LOTS_FILTER,
      termFilter: ALL_TERMS_FILTER,
      sortBy: 'newest',
      viewMode: 'cards',
      selectedIds: [],
      confirmingOrderIds: [],
      updatingLotOrderIds: [],
      drivers: [],
      accounts: [],
      referenceError: '',
      showAddModal: false,
      showLeavingModal: false,
      showBillingModal: false,
      showLotsModal: false,
      showExportModal: false,
      billingOrder: null,
      pageFeedback: '',
      pageFeedbackType: 'success'
    }
  },

  computed: {
    loading () {
      return !this.$store.state.deliveriesLoaded
    },
    hasFullAccess () {
      return Boolean(this.$store.state.userInfo && this.$store.state.userInfo.userType === 'full')
    },
    isViewer () {
      return Boolean(this.$store.state.authorization && this.$store.state.authorization.role === 'Viewer')
    },
    canWrite () {
      return this.hasFullAccess && !this.isViewer
    },
    canManageLots () {
      return this.canWrite && Boolean(this.$store.state.authorization && this.$store.state.authorization.role === 'Admin')
    },
    lotNames () {
      return this.$store.getters.inventoryLotNames
    },
    lotsLoaded () {
      return this.$store.state.inventoryLotsLoaded
    },
    allInventory () {
      return (this.$store.state.deliveries || []).filter(isInventoryOrder)
    },
    activeInventory () {
      return this.allInventory.filter(isActiveInventoryOrder)
    },
    unassignedLotCount () {
      return this.activeInventory.filter(order => !this.inventoryLotFor(order)).length
    },
    lotOptions () {
      const options = [{
        value: ALL_LOTS_FILTER,
        label: this.$t('Inventory.all_lots'),
        count: this.activeInventory.length
      }]

      this.lotNames.forEach(lot => {
        options.push({
          value: lot,
          label: lot,
          count: this.activeInventory.filter(order => this.inventoryLotFor(order) === lot).length
        })
      })

      if (this.unassignedLotCount) {
        options.push({
          value: UNASSIGNED_LOT_FILTER,
          label: this.$t('Inventory.unassigned_lot'),
          count: this.unassignedLotCount
        })
      }

      return options
    },
    lotScopedInventory () {
      if (this.lotFilter === ALL_LOTS_FILTER) return this.activeInventory
      if (this.lotFilter === UNASSIGNED_LOT_FILTER) {
        return this.activeInventory.filter(order => !this.inventoryLotFor(order))
      }
      return this.activeInventory.filter(order => this.inventoryLotFor(order) === this.lotFilter)
    },
    unassignedTermCount () {
      return this.activeInventory.filter(order => !this.inventoryTermFor(order)).length
    },
    termOptions () {
      const options = [{
        value: ALL_TERMS_FILTER,
        label: this.$t('Inventory.all_terms'),
        count: this.activeInventory.length
      }]

      INVENTORY_TERMS.forEach(term => {
        options.push({
          value: term,
          label: term === INVENTORY_TERM.LONG_TERM
            ? this.$t('Inventory.long_term')
            : this.$t('Inventory.short_term'),
          count: this.activeInventory.filter(order => this.inventoryTermFor(order) === term).length
        })
      })

      if (this.unassignedTermCount) {
        options.push({
          value: UNASSIGNED_TERM_FILTER,
          label: this.$t('Inventory.unassigned_term'),
          count: this.unassignedTermCount
        })
      }

      return options
    },
    inventoryScopedInventory () {
      if (this.termFilter === ALL_TERMS_FILTER) return this.lotScopedInventory
      if (this.termFilter === UNASSIGNED_TERM_FILTER) {
        return this.lotScopedInventory.filter(order => !this.inventoryTermFor(order))
      }
      return this.lotScopedInventory.filter(order => this.inventoryTermFor(order) === this.termFilter)
    },
    insideCount () {
      return this.inventoryScopedInventory.filter(order => order.dateEntered && !order.dateLeft).length
    },
    leftCount () {
      return this.inventoryScopedInventory.filter(order => order.dateEntered && order.dateLeft).length
    },
    toConfirmCount () {
      return this.inventoryScopedInventory.filter(canConfirmInventoryArrival).length
    },
    selectedConfirmationOrders () {
      return this.activeInventory.filter(order => {
        return this.selectedIds.includes(this.orderId(order)) && canConfirmInventoryArrival(order)
      })
    },
    selectedOrders () {
      return this.activeInventory.filter(order => {
        return this.selectedIds.includes(this.orderId(order)) &&
          canLeaveInventory(order) &&
          !canConfirmInventoryArrival(order)
      })
    },
    selectedActionCount () {
      return this.selectedConfirmationOrders.length + this.selectedOrders.length
    },
    confirmingSelectedArrivals () {
      return this.selectedConfirmationOrders.some(order => {
        return this.confirmingOrderIds.includes(this.orderId(order))
      })
    },
    stateOptions () {
      return [
        {value: 'all', label: this.$t('Inventory.filter_all'), count: this.inventoryScopedInventory.length},
        {value: 'to_confirm', label: this.$t('Inventory.filter_to_confirm'), count: this.toConfirmCount},
        {value: 'inside', label: this.$t('Inventory.state_inside'), count: this.insideCount},
        {value: 'left', label: this.$t('Inventory.filter_left'), count: this.leftCount}
      ]
    },
    filteredInventory () {
      const query = this.normalizeSearch(this.searchQuery)
      const orders = this.inventoryScopedInventory.filter(order => {
        const matchesState = this.stateFilter === 'all' ||
          (this.stateFilter === 'to_confirm' && canConfirmInventoryArrival(order)) ||
          (this.stateFilter === 'inside' && order.dateEntered && !order.dateLeft) ||
          (this.stateFilter === 'left' && order.dateEntered && order.dateLeft)
        if (!matchesState) return false
        if (!query) return true
        const searchable = [
          this.orderId(order),
          order.vin,
          order.sourceOrderId,
          order.numRef,
          order.source,
          this.$realName(order, 'payer'),
          order.inventoryLot,
          order.inventoryTerm,
          order.year,
          order.make,
          order.model,
          order.trim
        ].map(this.normalizeSearch).join(' ')
        return searchable.includes(query)
      })

      return orders.slice().sort((left, right) => {
        if (this.sortBy === 'oldest') return Number(left.dateEntered || 0) - Number(right.dateEntered || 0)
        if (this.sortBy === 'billing') {
          return String(this.$realName(left, 'payer') || '').localeCompare(String(this.$realName(right, 'payer') || ''), undefined, {sensitivity: 'base'})
        }
        return Number(right.dateEntered || 0) - Number(left.dateEntered || 0)
      })
    },
    pageFeedbackClass () {
      return this.pageFeedbackType === 'error'
        ? 'inventory-page__notice--error'
        : 'inventory-page__notice--success'
    }
  },

  watch: {
    activeInventory () {
      const eligibleIds = this.activeInventory
        .filter(order => canLeaveInventory(order) || canConfirmInventoryArrival(order))
        .map(this.orderId)
      this.selectedIds = this.selectedIds.filter(orderId => eligibleIds.includes(orderId))

      const availableLotFilters = this.lotOptions.map(option => option.value)
      if (!availableLotFilters.includes(this.lotFilter)) this.lotFilter = ALL_LOTS_FILTER

      const availableTermFilters = this.termOptions.map(option => option.value)
      if (!availableTermFilters.includes(this.termFilter)) this.termFilter = ALL_TERMS_FILTER
    },
    lotNames () {
      const availableLotFilters = this.lotOptions.map(option => option.value)
      if (!availableLotFilters.includes(this.lotFilter)) this.lotFilter = ALL_LOTS_FILTER
    }
  },

  async created () {
    this.$store.dispatch('loadInventoryLots')
    if (!this.$store.state.deliveriesLoaded) {
      await this.$store.dispatch('subscribeToDeliveries')
    }
    if (!this.$store.state.zonesLoaded) {
      await this.$store.dispatch('subscribeToZones')
    }
    if (this.canWrite) this.loadReferenceData()
  },

  methods: {
    orderId (order) {
      return String(order['.key'] || order.orderNumber || order.key || '')
    },
    normalizeSearch (value) {
      return String(value || '').trim().toLowerCase()
    },
    inventoryLotFor (order) {
      const lot = normalizeInventoryLotName(order && order.inventoryLot)
      return this.lotNames.includes(lot) ? lot : ''
    },
    inventoryTermFor (order) {
      const term = String((order && order.inventoryTerm) || '').trim()
      return isInventoryTerm(term) ? term : ''
    },
    imageFor (order) {
      return getInventoryImage(order, '')
    },
    toggleSelection (orderId) {
      if (!this.canWrite) return
      if (this.selectedIds.includes(orderId)) {
        this.selectedIds = this.selectedIds.filter(id => id !== orderId)
      } else {
        this.selectedIds.push(orderId)
      }
    },
    clearSelection () {
      this.selectedIds = []
    },
    clearFilters () {
      this.searchQuery = ''
      this.stateFilter = 'all'
      this.lotFilter = ALL_LOTS_FILTER
      this.termFilter = ALL_TERMS_FILTER
      this.sortBy = 'newest'
    },
    async changeLot ({order, inventoryLot}) {
      if (!this.canWrite) return
      const orderId = this.orderId(order)
      if (!orderId || this.updatingLotOrderIds.includes(orderId)) return

      this.updatingLotOrderIds.push(orderId)
      this.pageFeedback = ''
      try {
        await updateInventoryLot({
          companyUID: this.$store.state.authorization.company,
          orderId,
          inventoryLot,
          operator: {
            uid: (this.$store.state.user && this.$store.state.user.uid) || '',
            name: (this.$store.state.userInfo && this.$store.state.userInfo.name) ||
              (this.$store.state.user && (this.$store.state.user.displayName || this.$store.state.user.email)) || ''
          }
        })
        this.pageFeedback = this.$t('Inventory.lot_updated_success', {orderId, lot: inventoryLot})
        this.pageFeedbackType = 'success'
      } catch (error) {
        const errorCode = error && error.code ? ` (${error.code})` : ''
        if (error && error.code === 'INVALID_INVENTORY_LOT') this.$store.dispatch('loadInventoryLots', {force: true})
        this.pageFeedback = `${this.$t('Inventory.lot_update_error')}${errorCode}`
        this.pageFeedbackType = 'error'
      } finally {
        this.updatingLotOrderIds = this.updatingLotOrderIds.filter(id => id !== orderId)
      }
    },
    async confirmArrival (order) {
      if (!this.canWrite) return
      const orderId = this.orderId(order)
      if (!orderId || this.confirmingOrderIds.includes(orderId)) return

      this.confirmingOrderIds.push(orderId)
      this.pageFeedback = ''
      try {
        await confirmInventoryArrival({
          companyUID: this.$store.state.authorization.company,
          orderId,
          operator: {
            uid: (this.$store.state.user && this.$store.state.user.uid) || '',
            name: (this.$store.state.userInfo && this.$store.state.userInfo.name) ||
              (this.$store.state.user && (this.$store.state.user.displayName || this.$store.state.user.email)) || ''
          }
        })
        this.pageFeedback = this.$t('Inventory.arrival_confirmed_success', {orderId})
        this.pageFeedbackType = 'success'
      } catch (error) {
        const errorCode = error && error.code ? ` (${error.code})` : ''
        this.pageFeedback = `${this.$t('Inventory.arrival_confirm_error')}${errorCode}`
        this.pageFeedbackType = 'error'
      } finally {
        this.confirmingOrderIds = this.confirmingOrderIds.filter(id => id !== orderId)
      }
    },
    async confirmSelectedArrivals () {
      if (!this.canWrite || !this.selectedConfirmationOrders.length || this.confirmingSelectedArrivals) return

      const orders = this.selectedConfirmationOrders.slice()
      const orderIds = orders.map(this.orderId)
      this.confirmingOrderIds = this.confirmingOrderIds.concat(
        orderIds.filter(orderId => !this.confirmingOrderIds.includes(orderId))
      )
      this.pageFeedback = ''

      const operator = {
        uid: (this.$store.state.user && this.$store.state.user.uid) || '',
        name: (this.$store.state.userInfo && this.$store.state.userInfo.name) ||
          (this.$store.state.user && (this.$store.state.user.displayName || this.$store.state.user.email)) || ''
      }
      const results = await Promise.all(orders.map(async order => {
        const orderId = this.orderId(order)
        try {
          await confirmInventoryArrival({
            companyUID: this.$store.state.authorization.company,
            orderId,
            operator
          })
          return {orderId, success: true}
        } catch (error) {
          return {orderId, success: false, error}
        }
      }))

      const confirmedIds = results.filter(result => result.success).map(result => result.orderId)
      const failed = results.filter(result => !result.success)
      this.selectedIds = this.selectedIds.filter(orderId => !confirmedIds.includes(orderId))
      this.confirmingOrderIds = this.confirmingOrderIds.filter(orderId => !orderIds.includes(orderId))

      if (!failed.length) {
        this.pageFeedback = this.$t('Inventory.arrivals_confirmed_count', {count: confirmedIds.length})
        this.pageFeedbackType = 'success'
        return
      }

      const firstError = failed[0].error
      const errorCode = firstError && firstError.code ? ` (${firstError.code})` : ''
      this.pageFeedback = confirmedIds.length
        ? `${this.$t('Inventory.arrival_confirm_partial_error', {confirmed: confirmedIds.length, total: results.length})}${errorCode}`
        : `${this.$t('Inventory.arrival_confirm_error')}${errorCode}`
      this.pageFeedbackType = 'error'
    },
    openBillingModal (order) {
      if (!this.canWrite) return
      this.billingOrder = order
      this.showBillingModal = true
    },
    closeBillingModal () {
      this.showBillingModal = false
      this.billingOrder = null
    },
    async loadReferenceData () {
      this.referenceError = ''
      const errors = []
      await Promise.all([
        db.ref(`database/transporter/${this.$store.state.authorization.company}/liste`).once('value')
          .then(snapshot => {
            const drivers = []
            snapshot.forEach(child => {
              const value = child.val()
              drivers.push({
                uid: child.key,
                name: typeof value === 'string' ? value : (value && (value.name || value.value)) || child.key
              })
            })
            this.drivers = drivers.sort((left, right) => left.name.localeCompare(right.name))
          })
          .catch(() => errors.push(this.$t('Inventory.drivers_load_error'))),
        db.ref('users/pending-creation')
          .orderByChild('transporterUid')
          .equalTo(this.$store.state.authorization.company)
          .once('value')
          .then(snapshot => {
            const accounts = []
            snapshot.forEach(child => {
              const account = child.val() || {}
              if (account.status === 'Approved') accounts.push({...account, '.key': child.key})
            })
            this.accounts = accounts.sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))
          })
          .catch(() => errors.push(this.$t('Inventory.accounts_load_error')))
      ])
      this.referenceError = errors.join(' ')
    },
    onCreationCompleted (result) {
      const count = (result.createdOrderIds || []).length
      this.pageFeedback = result.allDispatchesQueued === false
        ? this.$t('Inventory.created_dispatch_partial_error')
        : this.$t('Inventory.created_count', {count})
      this.pageFeedbackType = result.allDispatchesQueued === false ? 'error' : 'success'
    },
    onLeavingCompleted (result) {
      const count = (result.updatedOrderIds || []).length
      this.clearSelection()
      this.pageFeedback = result.allDispatchesQueued === false
        ? this.$t('Inventory.departure_dispatch_partial_error')
        : this.$t('Inventory.departure_count', {count})
      this.pageFeedbackType = result.allDispatchesQueued === false ? 'error' : 'success'
    },
    onBillingCompleted () {
      this.closeBillingModal()
      this.pageFeedback = this.$t('Inventory.billing_done_success')
      this.pageFeedbackType = 'success'
    }
  }
}
</script>

<style scoped>
.inventory-page {
  min-height: 100vh;
  background: #f5f7f9;
}

.inventory-page__main {
  width: min(1500px, calc(100% - 40px));
  margin: 0 auto;
  padding: 26px 0 50px;
}

.inventory-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  overflow: hidden;
  position: relative;
  padding: 30px 34px;
  border-radius: 18px;
  color: #fff;
  background: linear-gradient(125deg, #273747 0%, #334b5d 58%, #93632e 130%);
  box-shadow: 0 12px 30px rgba(26, 39, 52, .16);
}

.inventory-hero::after {
  content: '';
  position: absolute;
  right: -80px;
  bottom: -120px;
  width: 310px;
  height: 310px;
  border: 45px solid rgba(255, 255, 255, .04);
  border-radius: 50%;
}

.inventory-hero__copy,
.inventory-hero__actions {
  position: relative;
  z-index: 1;
}

.inventory-hero__eyebrow {
  color: #f5b767;
  font-size: .75rem;
  font-weight: 800;
  letter-spacing: .13em;
  text-transform: uppercase;
}

.inventory-hero h1 {
  margin: 4px 0 7px;
  font-size: clamp(1.7rem, 3vw, 2.5rem);
  font-weight: 750;
}

.inventory-hero p {
  max-width: 650px;
  margin: 0;
  color: #dce4ea;
  font-size: .94rem;
}

.inventory-hero__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.inventory-button {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 15px;
  border-radius: 9px;
  font-weight: 700;
}

.inventory-button--primary {
  color: #253545;
  background: #ffad4c;
}

.inventory-button--primary:hover:not(:disabled) {
  background: #ffbd6f;
}

.inventory-button--secondary {
  color: #fff;
  border: 1px solid rgba(255, 255, 255, .35);
  background: rgba(255, 255, 255, .1);
}

.inventory-button--secondary:hover {
  color: #fff;
  background: rgba(255, 255, 255, .18);
}

.inventory-button__count {
  display: inline-flex;
  min-width: 21px;
  height: 21px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #fff;
  background: #273747;
  font-size: .7rem;
}

.inventory-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 18px 0;
}

.inventory-stat {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border: 1px solid #e4e9ed;
  border-radius: 13px;
  background: #fff;
}

.inventory-stat > i {
  display: flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 1.15rem;
}

.inventory-stat div {
  display: flex;
  flex-direction: column;
}

.inventory-stat strong {
  color: #293745;
  font-size: 1.35rem;
  line-height: 1.1;
}

.inventory-stat span {
  color: #74818e;
  font-size: .78rem;
  font-weight: 600;
}

.inventory-stat--inside i {
  color: #167149;
  background: #dff7e9;
}

.inventory-stat--left i {
  color: #8b5b10;
  background: #fff0ca;
}

.inventory-stat--visible i {
  color: #3b637f;
  background: #e5f0f7;
}

.inventory-read-only,
.inventory-page__notice {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 15px;
  margin-bottom: 14px;
  border-radius: 9px;
}

.inventory-read-only {
  color: #3c617a;
  background: #e8f2f8;
}

.inventory-page__notice .close {
  margin-left: auto;
}

.inventory-page__notice--warning {
  color: #79500b;
  background: #fff0c8;
}

.inventory-page__notice--success {
  color: #17633b;
  background: #e1f6e9;
}

.inventory-page__notice--error {
  color: #8b3034;
  background: #fde5e6;
}

.inventory-controls {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto auto auto;
  align-items: center;
  gap: 14px;
  padding: 14px;
  margin-bottom: 18px;
  border: 1px solid #e2e7eb;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 5px 16px rgba(27, 39, 51, .05);
}

.inventory-search {
  display: flex;
  align-items: center;
  position: relative;
  margin: 0;
}

.inventory-search > i {
  position: absolute;
  left: 13px;
  color: #84909b;
}

.inventory-search input {
  width: 100%;
  height: 42px;
  padding: 8px 42px 8px 38px;
  border: 1px solid #d8dee4;
  border-radius: 9px;
  outline: none;
}

.inventory-search input:focus {
  border-color: #e39a48;
  box-shadow: 0 0 0 3px rgba(227, 154, 72, .15);
}

.inventory-search button {
  position: absolute;
  right: 7px;
  border: 0;
  color: #7d8995;
  background: transparent;
}

.inventory-state-filter {
  display: flex;
  padding: 3px;
  border-radius: 9px;
  background: #eef1f4;
}

.inventory-state-filter button {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 7px 10px;
  border: 0;
  border-radius: 7px;
  color: #62707d;
  background: transparent;
  font-size: .78rem;
  font-weight: 700;
  white-space: nowrap;
}

.inventory-state-filter button.is-active {
  color: #30404f;
  background: #fff;
  box-shadow: 0 2px 7px rgba(28, 42, 54, .1);
}

.inventory-state-filter button span {
  min-width: 19px;
  padding: 2px 5px;
  border-radius: 999px;
  color: #6b7784;
  background: #e4e8ec;
  font-size: .65rem;
}

.inventory-sort {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  white-space: nowrap;
}

.inventory-sort span {
  color: #76828e;
  font-size: .75rem;
  font-weight: 700;
}

.inventory-sort select {
  height: 38px;
  padding: 5px 28px 5px 9px;
  border: 1px solid #d8dee4;
  border-radius: 8px;
  color: #465564;
  background: #fff;
  font-size: .8rem;
}

.inventory-view-toggle {
  display: flex;
  padding: 3px;
  border-radius: 9px;
  background: #eef1f4;
}

.inventory-view-toggle button {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 9px;
  border: 0;
  border-radius: 7px;
  color: #697683;
  background: transparent;
  font-size: .74rem;
  font-weight: 700;
  white-space: nowrap;
}

.inventory-view-toggle button.is-active {
  color: #30404f;
  background: #fff;
  box-shadow: 0 2px 7px rgba(28, 42, 54, .1);
}

.inventory-view-toggle button.is-active i {
  color: #d97918;
}

.inventory-lot-filter {
  display: flex;
  grid-column: 1 / -1;
  align-items: flex-start;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #edf0f3;
}

.inventory-lot-filter__label {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  color: #65727f;
  font-size: .76rem;
  font-weight: 750;
  white-space: nowrap;
}

.inventory-lot-filter__label i {
  color: #d97918;
}

.inventory-lot-filter__options {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.inventory-lot-filter__options button {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border: 1px solid #dce2e7;
  border-radius: 8px;
  color: #5c6976;
  background: #f8f9fa;
  font-size: .75rem;
  font-weight: 700;
}

.inventory-lot-filter__options button:hover {
  border-color: #e5b37c;
  background: #fff8ef;
}

.inventory-lot-filter__options button.is-active {
  border-color: #d97918;
  color: #6f420f;
  background: #fff0df;
  box-shadow: 0 0 0 2px rgba(217, 121, 24, .09);
}

.inventory-lot-filter__options button span {
  min-width: 19px;
  padding: 1px 5px;
  border-radius: 999px;
  color: #687582;
  background: #e8ecef;
  font-size: .65rem;
  text-align: center;
}

.inventory-lot-filter__options button.is-active span {
  color: #fff;
  background: #d97918;
}

.inventory-lot-filter__options .inventory-lot-filter__manage {
  border-style: dashed;
  color: #6e7c89;
}

.inventory-lot-filter__options .inventory-lot-filter__manage i {
  margin-right: 4px;
}

.inventory-lot-filter__empty {
  align-self: center;
  color: #a5671a;
  font-size: .82rem;
}

.inventory-term-filter .inventory-lot-filter__label i {
  color: #3e718e;
}

.inventory-term-filter .inventory-lot-filter__options button:hover {
  border-color: #9bbdce;
  background: #f1f8fb;
}

.inventory-term-filter .inventory-lot-filter__options button.is-active {
  border-color: #4b819e;
  color: #28566e;
  background: #eaf5fa;
  box-shadow: 0 0 0 2px rgba(75, 129, 158, .09);
}

.inventory-term-filter .inventory-lot-filter__options button.is-active span {
  background: #4b819e;
}

.inventory-selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  position: sticky;
  top: 8px;
  z-index: 5;
  padding: 11px 15px;
  margin-bottom: 16px;
  border: 1px solid #edb779;
  border-radius: 11px;
  color: #704710;
  background: #fff5e8;
  box-shadow: 0 5px 18px rgba(64, 46, 25, .1);
}

.inventory-selection-bar > div {
  display: flex;
  align-items: center;
  gap: 9px;
}

.inventory-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 20px;
}

.inventory-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.inventory-page__loading,
.inventory-empty {
  display: flex;
  min-height: 320px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 35px;
  border: 1px dashed #d3d9df;
  border-radius: 16px;
  color: #75818d;
  background: #fff;
  text-align: center;
}

.inventory-page__loading i {
  margin-bottom: 12px;
  color: #dd8527;
  font-size: 2rem;
}

.inventory-empty--compact {
  min-height: 260px;
}

.inventory-empty__icon {
  display: flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  border-radius: 50%;
  color: #7f8d99;
  background: #edf1f4;
  font-size: 2rem;
}

.inventory-empty h2 {
  margin: 0 0 7px;
  color: #354352;
  font-size: 1.25rem;
}

.inventory-empty p {
  max-width: 470px;
  margin-bottom: 17px;
}

@media (max-width: 1050px) {
  .inventory-controls {
    grid-template-columns: 1fr auto auto;
  }

  .inventory-search {
    grid-column: 1 / -1;
  }
}

@media (max-width: 760px) {
  .inventory-page__main {
    width: min(100% - 22px, 1500px);
    padding-top: 14px;
  }

  .inventory-hero {
    align-items: stretch;
    flex-direction: column;
    padding: 23px;
  }

  .inventory-hero__actions {
    align-items: stretch;
  }

  .inventory-hero__actions .btn {
    flex: 1;
  }

  .inventory-stats {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .inventory-stat {
    padding: 12px 14px;
  }

  .inventory-controls {
    grid-template-columns: 1fr;
  }

  .inventory-search {
    grid-column: auto;
  }

  .inventory-state-filter {
    overflow-x: auto;
  }

  .inventory-state-filter button {
    flex: 1;
  }

  .inventory-sort {
    justify-content: space-between;
  }

  .inventory-sort select {
    flex: 1;
  }

  .inventory-view-toggle {
    justify-self: stretch;
  }

  .inventory-view-toggle button {
    flex: 1;
  }

  .inventory-lot-filter {
    align-items: stretch;
    flex-direction: column;
    gap: 6px;
  }

  .inventory-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 500px) {
  .inventory-hero__actions,
  .inventory-selection-bar,
  .inventory-selection-bar > div:last-child {
    flex-direction: column;
  }

  .inventory-selection-bar,
  .inventory-selection-bar .btn {
    width: 100%;
  }

  .inventory-state-filter button {
    min-width: max-content;
  }
}
</style>
